import React, { useEffect, useState } from "react";
import s from "./CreateCode.module.scss";
import { Controller, useForm } from "react-hook-form";
import { Button, DatePicker, Input, message, Select, TimePicker } from "antd";
import { useQuery } from "../../../../../../libs/common";
import clsx from "clsx";
import ArrowDown from "../../../../../../global components/Icons/ArrowDown";
import dayjs from "dayjs";
import { Dayjs } from "dayjs";
import { UseAuthToken } from "../../../../../../libs/api/userSession";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
  parseClientError,
  swrFetcher,
  swrMutationFetcher,
  SwrMutationFetcherArgType,
} from "../../../../../../libs/api/Endpoints";
import { SuccessDataType } from "../../../../../../libs/api/types";
import { generateResult } from "../../../../../../global components/ResultModal/ResultModal";
import {
  PromoCode,
  PromoCodeResponse,
} from "../../../../../../libs/Utils/Types/PromoCode";
import { useNavigate } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import ReusableDrawer from "../../../../../../global components/Drawer/ReuseableDrawer";
import Recipients from "../Recipients/Recipients";
import Add from "../../../../../../global components/Icons/Add";
import Tags from "../../../../../../global components/Tags/Tags";
import { UsersApiResponse } from "../../../../../../libs/Utils/Types/User";
import Item from "antd/es/list/Item";

const { Option } = Select;

type Inputs = {
  description: string;
  code: string;
  discount: string;
  usageLimit: string;
  startTime: string;
  startDate: string;
  endTime: string;
  endDate: string;
  discountType: string;
  maxCredit: string;
};

function CreateCode() {
  const query = useQuery();
  const codeId = query.get("codeId");
  const token = UseAuthToken();
  const [users, setUsers] = useState<string[]>();
  const [openDrawer, setOpen] = useState<boolean>(false);
  const [tags, setTags] = useState<{ key: string; label: string }[]>([]);

  const { trigger: createCode, isMutating } = useSWRMutation(
    `api/admin/promo-codes/${codeId ? codeId : ""}`,
    swrMutationFetcher,
  );
  const {
    data,
    isLoading,
    error,
    mutate: Mutate,
  } = useSWR<{ data: PromoCode }, any, any>(
    token ? [`api/admin/promo-codes/${codeId}`, token] : null,
    swrFetcher,
    {
      isPaused: () => !codeId,
    },
  );
  const [searchValue /*setSearchValue*/] = useState("");
  const [currentPage /*setCurrentPage*/] = useState(1);
  const [filter /*setFilter*/] = useState<string>("");
  const { data: TableUsers } = useSWR<{ data: UsersApiResponse }>(
    token ? ["api/admin/promo-codes/users", token] : null,
    swrFetcher,
    { shouldRetryOnError: false },
  );
  const { mutate } = useSWR<PromoCodeResponse, any, any>(
    token
      ? [
          "api/admin/promo-codes",
          token,
          {
            page: currentPage,
            ...(filter && { status: filter }),
            search: searchValue,
          },
        ]
      : null,
    swrFetcher,
  );
  const {
    watch,
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<Inputs>({ shouldFocusError: false });
  const combineDateTime = (
    date: string | Dayjs | null,
    time: string | Dayjs | null,
  ) => {
    if (date && time) {
      const parsedDate = dayjs(date); // Convert to Dayjs if it's a string
      const parsedTime = dayjs(time); // Convert to Dayjs if it's a string

      if (parsedDate.isValid() && parsedTime.isValid()) {
        const combined = parsedDate
          .hour(parsedTime.hour())
          .minute(parsedTime.minute())
          .second(0)
          .millisecond(0);

        return combined.toISOString();
      }
    }
    return null; // Handle invalid or null inputs
  };
  const handleUsers = (user: string[]) => {
    setUsers(user);
  };
  const handleDrawer = () => {
    setOpen((prev) => !prev);
  };
  useEffect(() => {
    if (data) {
      setValue("code", data?.data.name);
      setValue("description", data?.data.description);
      if (data?.data.creditPercentage)
        setValue("discount", data?.data.creditPercentage?.toString());
      setValue("discountType", data.data.type);
      setValue("endDate", dayjs(data.data.endDate).toISOString());
      setValue("endTime", dayjs(data?.data.endDate).toISOString());
      setValue("maxCredit", data.data.maxCredit.toString());
      setValue("startDate", dayjs(data.data.startDate).toISOString());
      setValue("startTime", dayjs(data.data.startDate).toISOString());
      setValue("usageLimit", data.data.usageLimit.toString());
    }
  }, [data, setValue]);
  const status = data?.data.status;
  const selectAfter = (
    <Controller
      name="discountType"
      control={control}
      defaultValue={data?.data.type || "percentage"}
      render={({ field }) => (
        <Select
          disabled={data?.data.status === "active"}
          {...field}
          className={s.select}
          suffixIcon={<ArrowDown width={9} height={5} fill="#6A6767" />}
        >
          <Option value="amount">Fixed amount</Option>
          <Option value="percentage">Percentage</Option>
        </Select>
      )}
    />
  );
  const navigate = useNavigate();
  const onSubmit = async (data: Inputs) => {
    try {
      const jsonBody = {
        code: data.code,
        type: data.discountType,
        discountInPercentage: data.discount,

        maxCredit: data.maxCredit,
        description: data.description,
        limitPerUser: data.usageLimit,
        endDate: combineDateTime(data.endDate, data.endTime),
        recipients: users,

        ...(status !== "active" && {
          startDate: combineDateTime(data.startDate, data.startTime),
        }),
      };

      const fetcherArg: SwrMutationFetcherArgType = {
        token: token,
        requestOption: {
          method: codeId ? "PATCH" : "POST",
          body: JSON.stringify(jsonBody),
          headers: {
            "Content-Type": "application/json",
          },
        },
      };
      const result: SuccessDataType<any> = await createCode(fetcherArg);
      mutate();
      Mutate();
      generateResult(
        "success",
        result.message.charAt(0).toUpperCase() + result.message.slice(1),
      );
      navigate("/dashboard/promo-code");
    } catch (error) {
      const e = parseClientError(error);
      generateResult(
        "error",
        e.message.charAt(0).toUpperCase() + e.message.slice(1) ||
          "Something went wrong",
      );
    }
  };
  if (error) {
    const e = parseClientError(error);
    message.error(
      e.message.charAt(0).toUpperCase() + e.message.slice(1) ||
        "Something went wrong",
    );
  }
  const handleTagsChange = (updatedTags: { key: string; label: string }[]) => {
    const updatedIds = updatedTags.map((tag) => tag.key);

    console.log(updatedTags);
    setTags(updatedTags);
    handleUsers(updatedIds);
  };
  useEffect(() => {
    if (data?.data.recipients) {
      handleUsers(data.data.recipients.map((item) => item.id));
    }
  }, [data?.data.recipients]);
  useEffect(() => {
    const recipients = data?.data?.recipients;
    const users = TableUsers?.data?.data;

    if (!Array.isArray(recipients) || !Array.isArray(users)) return;

    const recipientIds = new Set(recipients.map((r) => r.id));

    const matchedTags = users
      .filter((user) => recipientIds.has(user.id))
      .map((user) => ({
        key: user.id,
        label: `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim(),
      }));

    setTags(matchedTags);
  }, [data?.data?.recipients, TableUsers?.data?.data]);

  return isLoading || error ? (
    <div className={s.spin}>
      <ThreeDots
        visible={true}
        height="50"
        width="50"
        color="#d52b1e"
        radius="9"
        ariaLabel="three-dots-loading"
        wrapperStyle={{
          dispaly: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
        wrapperClass=""
      />
    </div>
  ) : (
    <>
      <ReusableDrawer
        title="Add recipients"
        Open={openDrawer}
        handleOpen={handleDrawer}
        maskClosable={false}
      >
        <Recipients
          codeId={codeId || ""}
          handleTags={handleTagsChange}
          handleDrawer={handleDrawer}
          handleUsers={handleUsers}
        />
      </ReusableDrawer>
      <div className={s.wrapper}>
        <h2 className={s.title}>
          {data?.data.id ? "Edit" : "Create"} promo code
        </h2>
        <form className={s.form} onSubmit={handleSubmit(onSubmit)}>
          <div className={clsx(s.inputWrapper)}>
            <h5 className={clsx(s.inputLabel)}>Description</h5>
            <input
              type="text"
              defaultValue={data?.data.description}
              placeholder="Enter description"
              className={clsx(s.input, { [s.inputError]: errors.description })}
              {...register("description", {
                required: "Description is required",
              })}
            />
            <span className={s.helperText}>
              <p className={s.titleMsg}>
                This is just an internal title to help organize and locate your
                coupons easily.
              </p>
            </span>
            {errors.description && (
              <span className={clsx(s.errorMessage)}>
                {errors.description.message}
              </span>
            )}
          </div>

          <div className={s.inputWrapper}>
            <h5 className={s.label}>Code</h5>
            <Controller
              name="code"
              control={control}
              rules={{ required: "Code is required" }}
              render={({ field }) => (
                <Input
                  defaultValue={data?.data.name}
                  disabled={data?.data.status === "active"}
                  {...field}
                  placeholder="Enter code"
                  className={clsx(s.input, { [s.inputError]: errors.code })}
                  maxLength={20}
                  count={{
                    show: true,
                    max: 20,
                  }}
                />
              )}
            />
            {errors.code && (
              <span className={clsx(s.errorMessage)}>
                {errors.code.message}
              </span>
            )}
          </div>

          <div className={s.inputWrapper}>
            <h5 className={s.label}>Discount value</h5>
            <Controller
              name="discount"
              control={control}
              rules={
                watch("discountType") === "percentage"
                  ? { required: "Discount is required" }
                  : {}
              }
              render={({ field }) => (
                <Input
                  disabled={data?.data.status === "active"}
                  {...field}
                  defaultValue={data?.data.creditPercentage}
                  style={{ border: 0 }}
                  placeholder="Enter amount"
                  addonAfter={selectAfter}
                  className={clsx(s.discountInput, {
                    [s.inputError]: errors.discount,
                  })}
                />
              )}
            />
            {errors.discount && (
              <span className={clsx(s.errorMessage)}>
                {errors.discount.message}
              </span>
            )}
          </div>
          <div className={clsx(s.inputWrapper)}>
            <h5 className={clsx(s.inputLabel)}>Usage limit</h5>
            <input
              min="0"
              defaultValue={data?.data.usageLimit}
              type="number"
              placeholder="Number of times a user can use the code"
              className={clsx(s.input, { [s.inputError]: errors.usageLimit })}
              {...register("usageLimit", {
                required: "Usage limit is required",
                min: { value: 0, message: "Usage limit cannot be less than 0" },
              })}
            />

            {errors.usageLimit && (
              <span className={clsx(s.errorMessage)}>
                {errors.usageLimit.message}
              </span>
            )}
          </div>
          <div className={clsx(s.inputWrapper)}>
            <h5 className={clsx(s.inputLabel)}>Max credit</h5>
            <input
              min="0"
              type="number"
              disabled={data?.data.status === "active"}
              defaultValue={data?.data.maxCredit}
              placeholder="The maximum credit applicable"
              className={clsx(s.input, { [s.inputError]: errors.maxCredit })}
              {...register("maxCredit", {
                required: "Max credit is required",
                min: { value: 0, message: "Usage limit cannot be less than 0" },
              })}
            />

            {errors.usageLimit && (
              <span className={clsx(s.errorMessage)}>
                {errors.usageLimit.message}
              </span>
            )}
          </div>
          <div className={clsx(s.dateWrapper)}>
            <div className={s.date}>
              <h5 className={clsx(s.inputLabel)}>Start date</h5>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    disabled={data?.data.status === "active"}
                    defaultValue={
                      codeId &&
                      data?.data.startDate &&
                      dayjs(data.data.startDate) // Safely handle undefined
                    }
                    value={
                      field.value && dayjs(field.value) // Ensure value is a Dayjs object
                    }
                    onChange={(date, dateString) => {
                      if (date) {
                        const isValidDate = dayjs(date.toString()).isValid();
                        if (isValidDate) {
                          field.onChange(date); // Set the value if valid
                        } else {
                        }
                      }
                    }}
                    className={clsx(s.time, {
                      [s.inputError]: errors.startDate,
                    })}
                    placeholder="MM/YYYY"
                  />
                )}
              />

              {errors.startDate && (
                <span className={clsx(s.errorMessage)}>
                  {errors.startDate.message}
                </span>
              )}
            </div>

            <div className={s.date}>
              <h5 className={clsx(s.inputLabel)}>Start time</h5>
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => (
                  <TimePicker
                    disabled={data?.data.status === "active"}
                    defaultValue={
                      codeId ? dayjs(data?.data.startDate) : null // Convert to Dayjs and extract time
                    }
                    className={clsx(s.time)}
                    onChange={(time, timeString) => {
                      if (time) {
                        field.onChange(time); // Set the value
                      }
                    }}
                    changeOnScroll
                    format="HH:mm"
                    needConfirm={true}
                    suffixIcon={
                      <ArrowDown height={16} width={16} fill="#6A6767" />
                    }
                  />
                )}
              />

              {errors.startTime && (
                <span className={clsx(s.errorMessage)}>
                  {errors.startTime.message}
                </span>
              )}
            </div>
          </div>
          <div className={clsx(s.dateWrapper)}>
            <div className={s.date}>
              <h5 className={clsx(s.inputLabel)}>End date</h5>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    defaultValue={
                      codeId && data?.data.endDate && dayjs(data?.data.endDate) // Safely handle undefined
                    }
                    value={
                      field.value && dayjs(field.value) // Ensure value is a Dayjs object
                    }
                    onChange={(date, dateString) => {
                      if (date) {
                        const isValidDate = dayjs(date.toString()).isValid();
                        if (isValidDate) {
                          field.onChange(date); // Set the value if valid
                        } else {
                        }
                      }
                    }}
                    className={clsx(s.time, {
                      [s.inputError]: errors.endDate,
                    })}
                    placeholder="MM/YYYY"
                  />
                )}
              />

              {errors.endDate && (
                <span className={clsx(s.errorMessage)}>
                  {errors.endDate.message}
                </span>
              )}
            </div>

            <div className={s.date}>
              <h5 className={clsx(s.inputLabel)}>End time</h5>
              <Controller
                name="endTime"
                control={control}
                render={({ field }) => (
                  <TimePicker
                    defaultValue={
                      codeId ? dayjs(data?.data.endDate) : null // Convert to Dayjs and extract time
                    }
                    className={clsx(s.time)}
                    onChange={(time, timeString) => {
                      if (time) {
                        field.onChange(time); // Set the value
                      }
                    }}
                    changeOnScroll
                    format="HH:mm"
                    needConfirm={true}
                    suffixIcon={
                      <ArrowDown height={16} width={16} fill="#6A6767" />
                    }
                  />
                )}
              />

              {errors.endTime && (
                <span className={clsx(s.errorMessage)}>
                  {errors.endTime.message}
                </span>
              )}
            </div>
          </div>
          <hr />
          <div className={s.inputWrapper}>
            <div className={s.label}>
              <h4>Recipients</h4>
              <button
                type="button"
                // disabled={data?.status === "active"}
                className={s.button}
                onClick={() =>
                  data?.data.status === "active"
                    ? generateResult(
                        "error",
                        "You can not add recipients to an active promo code.",
                      )
                    : handleDrawer()
                }
              >
                <Add height={11} width={11} fill="#D52B1E" />
                Add recipient
              </button>
            </div>
            <span className={s.helperText}>
              <p className={s.titleMsg}>
                {users && users?.length > 0
                  ? users?.length + " " + "recipients added"
                  : "No recipients added"}
              </p>
            </span>
            {tags.length > 0 && (
              <div className={s.tags}>
                <Tags initialTags={tags} onTagsChange={handleTagsChange} />
              </div>
            )}
          </div>
          <Button
            loading={isMutating}
            className={s.submitBtn}
            htmlType="submit"
          >
            {codeId ? "Save Changes" : "Create promo code"}
          </Button>
        </form>
      </div>
    </>
  );
}

export default CreateCode;
